import * as React from "react";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, CheckCircle2, AlertTriangle, FileText, History, Plus, Loader2, Undo2, ListChecks, Clock } from "lucide-react";
import { useFinance } from "@/context/FinanceContext";
import { parseOFX, OFXTransaction } from "@/lib/ofxParser";
import {
  matchOFXAgainstTransactions,
  useOFXReconciliation,
  MatchedRow,
  UnmatchedRow,
} from "@/hooks/finance/useOFXReconciliation";
import { useToast } from "@/hooks/use-toast";
import { TransactionForm } from "@/components/TransactionForm";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";

const fmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const formatDateBR = (iso?: string) => {
  if (!iso) return "-";
  try { return format(parseISO(iso), "dd/MM/yyyy"); } catch { return iso; }
};

export default function ConciliacaoOFX() {
  const { accounts, transactions, forceRefresh } = useFinance();
  const { reconcileMany, createImportRecord, undoImport } = useOFXReconciliation();
  const { toast } = useToast();

  const [accountId, setAccountId] = React.useState<string>("");
  const [fileName, setFileName] = React.useState<string>("");
  const [ofxData, setOfxData] = React.useState<{
    bankId?: string;
    periodStart?: string;
    periodEnd?: string;
    transactions: OFXTransaction[];
  } | null>(null);
  const [matched, setMatched] = React.useState<MatchedRow[]>([]);
  const [unmatched, setUnmatched] = React.useState<UnmatchedRow[]>([]);
  const [alreadyImported, setAlreadyImported] = React.useState<MatchedRow[]>([]);
  const [processing, setProcessing] = React.useState(false);
  const [history, setHistory] = React.useState<any[]>([]);
  const [createForOFX, setCreateForOFX] = React.useState<OFXTransaction | null>(null);

  React.useEffect(() => {
    document.title = "Conciliação Bancária | Radar Financeiro";
  }, []);

  const loadHistory = React.useCallback(async () => {
    const { data, error } = await supabase
      .from("ofx_imports" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (!error && data) setHistory(data as any[]);
  }, []);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const recompute = React.useCallback((list: OFXTransaction[], acc: string) => {
    const res = matchOFXAgainstTransactions(list, transactions, acc);
    setMatched(res.matched);
    setUnmatched(res.unmatched);
    setAlreadyImported(res.alreadyImported);
  }, [transactions]);

  const handleFile = async (file: File) => {
    if (!accountId) {
      toast({ variant: "destructive", title: "Selecione uma conta", description: "Escolha a conta antes de importar." });
      return;
    }
    const text = await file.text();
    const parsed = parseOFX(text);
    if (parsed.transactions.length === 0) {
      toast({ variant: "destructive", title: "OFX vazio ou inválido", description: "Não foi possível ler transações deste arquivo." });
      return;
    }
    setFileName(file.name);
    setOfxData(parsed);
    recompute(parsed.transactions, accountId);
    toast({ title: "OFX carregado", description: `${parsed.transactions.length} transações lidas.` });
  };

  const onAccountChange = (acc: string) => {
    setAccountId(acc);
    if (ofxData) recompute(ofxData.transactions, acc);
  };

  const toggleMatchedSelection = (idx: number, value: boolean) => {
    setMatched((prev) => prev.map((r, i) => i === idx ? { ...r, selected: value } : r));
  };

  const changeMatchTarget = (idx: number, txId: string) => {
    setMatched((prev) => prev.map((r, i) => i === idx ? { ...r, selectedMatchId: txId } : r));
  };

  const selectedCount = matched.filter(m => m.selected && m.selectedMatchId).length;

  // Estatísticas e lista de lançamentos da conta selecionada (exclui transferências)
  const accountTransactions = React.useMemo(
    () => transactions.filter(t => t.accountId === accountId && t.kind !== "transferencia"),
    [transactions, accountId]
  );
  const reconciledAccountTx = React.useMemo(
    () => accountTransactions
      .filter(t => t.reconciled === true)
      .sort((a, b) => (b.paymentDate || b.dueDate || "").localeCompare(a.paymentDate || a.dueDate || "")),
    [accountTransactions]
  );
  const accountTotalCount = accountTransactions.length;
  const accountReconciledCount = reconciledAccountTx.length;
  const accountPendingCount = accountTotalCount - accountReconciledCount;

  const handleUndoSingle = async (transactionId: string) => {
    if (!confirm("Desfazer a conciliação deste lançamento? Ele voltará ao status 'Pago' e poderá ser editado.")) return;
    setProcessing(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({
          reconciled: false,
          reconciled_at: null,
          ofx_fitid: null,
          ofx_import_id: null,
        } as any)
        .eq("id", transactionId);
      if (error) throw error;
      toast({ title: "Conciliação desfeita", description: "O lançamento voltou ao status 'Pago'." });
      await forceRefresh();
      if (ofxData) recompute(ofxData.transactions, accountId);
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erro ao desfazer", description: e?.message || "Tente novamente." });
    } finally {
      setProcessing(false);
    }
  };

  const handleReconcile = async () => {
    if (!ofxData || selectedCount === 0) return;
    setProcessing(true);
    try {
      const importId = await createImportRecord({
        accountId,
        fileName,
        bankId: ofxData.bankId,
        periodStart: ofxData.periodStart,
        periodEnd: ofxData.periodEnd,
        totalTransactions: ofxData.transactions.length,
        matchedCount: selectedCount,
        createdCount: 0,
        skippedCount: unmatched.length,
      });

      const rows = matched
        .filter(m => m.selected && m.selectedMatchId)
        .map(m => ({ transactionId: m.selectedMatchId!, ofx: m.ofx }));

      const result = await reconcileMany(rows, importId);
      await forceRefresh();
      await loadHistory();

      if (result.fail === 0) {
        toast({ title: "✓ Conciliação concluída", description: `${result.ok} lançamentos conciliados.` });
        // Re-run match to update view
        if (ofxData) recompute(ofxData.transactions, accountId);
      } else {
        toast({
          variant: "destructive",
          title: `Concluído com ${result.fail} erro(s)`,
          description: `${result.ok} ok, ${result.fail} falharam.`,
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleUndoImport = async (id: string) => {
    if (!confirm("Desfazer esta importação? Os lançamentos voltarão ao status 'Pago'.")) return;
    setProcessing(true);
    const ok = await undoImport(id);
    if (ok) {
      toast({ title: "Importação desfeita" });
      await forceRefresh();
      await loadHistory();
      if (ofxData) recompute(ofxData.transactions, accountId);
    } else {
      toast({ variant: "destructive", title: "Erro ao desfazer importação" });
    }
    setProcessing(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="page-bleed py-4 md:py-8 space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Conciliação Bancária</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            Importe o extrato OFX do banco e concilie com seus lançamentos.
          </p>
        </div>

        {/* Upload area */}
        <Card>
          <CardContent className="p-4 md:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium">Conta bancária</label>
                <Select value={accountId} onValueChange={onAccountChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione a conta" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <input
                  id="ofx-file"
                  type="file"
                  accept=".ofx,.OFX,text/plain"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.target.value = "";
                  }}
                />
                <Button
                  onClick={() => document.getElementById("ofx-file")?.click()}
                  disabled={!accountId}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Importar arquivo OFX
                </Button>
              </div>
            </div>

            {accountId && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                <StatTile label="Total na conta" value={accountTotalCount} icon={<ListChecks className="h-4 w-4" />} />
                <StatTile label="Conciliados" value={accountReconciledCount} tone="success" icon={<CheckCircle2 className="h-4 w-4" />} />
                <StatTile label="Faltam conciliar" value={accountPendingCount} tone="warning" icon={<Clock className="h-4 w-4" />} />
              </div>
            )}

            {ofxData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t pt-4">
                <StatTile label="Total no OFX" value={ofxData.transactions.length} icon={<FileText className="h-4 w-4" />} />
                <StatTile label="Casadas" value={matched.length} tone="success" icon={<CheckCircle2 className="h-4 w-4" />} />
                <StatTile label="Sem match" value={unmatched.length} tone="warning" icon={<AlertTriangle className="h-4 w-4" />} />
                <StatTile label="Já conciliadas" value={alreadyImported.length} tone="muted" icon={<History className="h-4 w-4" />} />
              </div>
            )}
            {ofxData && (
              <p className="text-xs text-muted-foreground">
                Arquivo: <strong>{fileName}</strong>
                {ofxData.bankId && <> · Banco: {ofxData.bankId}</>}
                {ofxData.periodStart && <> · Período: {formatDateBR(ofxData.periodStart)} – {formatDateBR(ofxData.periodEnd)}</>}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Tabs sempre disponíveis quando há conta selecionada */}
        {accountId && (
          <Tabs defaultValue={ofxData ? "matched" : "reconciled"} className="space-y-4">
            <TabsList>
              {ofxData && <TabsTrigger value="matched">Casadas ({matched.length})</TabsTrigger>}
              {ofxData && <TabsTrigger value="unmatched">Sem match ({unmatched.length})</TabsTrigger>}
              {ofxData && <TabsTrigger value="already">Já conciliadas ({alreadyImported.length})</TabsTrigger>}
              <TabsTrigger value="reconciled">Conciliados na conta ({accountReconciledCount})</TabsTrigger>
              <TabsTrigger value="history">Histórico</TabsTrigger>
            </TabsList>

            <TabsContent value="matched">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                  <CardTitle className="text-base">Revise e confirme</CardTitle>
                  <Button
                    onClick={handleReconcile}
                    disabled={selectedCount === 0 || processing}
                    className="gap-2"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Conciliar selecionadas ({selectedCount})
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-3 w-10"></th>
                        <th className="p-3">Data OFX</th>
                        <th className="p-3">Descrição banco</th>
                        <th className="p-3 text-right">Valor</th>
                        <th className="p-3">Lançamento correspondente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matched.map((row, idx) => (
                        <tr key={row.ofx.fitid} className="border-t hover:bg-muted/20">
                          <td className="p-3">
                            <Checkbox
                              checked={row.selected}
                              onCheckedChange={(c) => toggleMatchedSelection(idx, !!c)}
                            />
                          </td>
                          <td className="p-3 whitespace-nowrap">{formatDateBR(row.ofx.date)}</td>
                          <td className="p-3">{row.ofx.memo || row.ofx.name || "-"}</td>
                          <td className={`p-3 text-right font-medium ${row.ofx.amount < 0 ? "text-destructive" : "text-emerald-600"}`}>
                            {fmt.format(row.ofx.amount)}
                          </td>
                          <td className="p-3">
                            {row.matches.length === 1 ? (
                              <SingleMatchPill tx={row.matches[0]} />
                            ) : (
                              <Select
                                value={row.selectedMatchId || ""}
                                onValueChange={(v) => changeMatchTarget(idx, v)}
                              >
                                <SelectTrigger className="h-8 w-full max-w-md">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {row.matches.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>
                                      {formatDateBR(m.dueDate)} · {m.description} · {fmt.format(m.value)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                        </tr>
                      ))}
                      {matched.length === 0 && (
                        <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhuma transação casada automaticamente.</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="unmatched">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Transações sem lançamento correspondente</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-3">Data</th>
                        <th className="p-3">Descrição</th>
                        <th className="p-3 text-right">Valor</th>
                        <th className="p-3 w-44"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.map((row) => (
                        <tr key={row.ofx.fitid} className="border-t hover:bg-muted/20">
                          <td className="p-3 whitespace-nowrap">{formatDateBR(row.ofx.date)}</td>
                          <td className="p-3">{row.ofx.memo || row.ofx.name || "-"}</td>
                          <td className={`p-3 text-right font-medium ${row.ofx.amount < 0 ? "text-destructive" : "text-emerald-600"}`}>
                            {fmt.format(row.ofx.amount)}
                          </td>
                          <td className="p-3">
                            <Button size="sm" variant="outline" className="gap-2" onClick={() => setCreateForOFX(row.ofx)}>
                              <Plus className="h-3.5 w-3.5" /> Criar lançamento
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {unmatched.length === 0 && (
                        <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Tudo casou! 🎉</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="already">
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-3">Data</th>
                        <th className="p-3">Descrição banco</th>
                        <th className="p-3 text-right">Valor</th>
                        <th className="p-3">Lançamento conciliado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alreadyImported.map((row) => (
                        <tr key={row.ofx.fitid} className="border-t">
                          <td className="p-3 whitespace-nowrap">{formatDateBR(row.ofx.date)}</td>
                          <td className="p-3">{row.ofx.memo || row.ofx.name || "-"}</td>
                          <td className="p-3 text-right">{fmt.format(row.ofx.amount)}</td>
                          <td className="p-3">{row.alreadyReconciled?.description}</td>
                        </tr>
                      ))}
                      {alreadyImported.length === 0 && (
                        <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Nenhuma transação já conciliada.</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reconciled">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Lançamentos conciliados desta conta
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-3">Pagamento</th>
                        <th className="p-3">Vencimento</th>
                        <th className="p-3">Descrição</th>
                        <th className="p-3 text-right">Valor</th>
                        <th className="p-3 w-44"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconciledAccountTx.map((t) => (
                        <tr key={t.id} className="border-t hover:bg-muted/20">
                          <td className="p-3 whitespace-nowrap">{formatDateBR(t.paymentDate)}</td>
                          <td className="p-3 whitespace-nowrap">{formatDateBR(t.dueDate)}</td>
                          <td className="p-3">{t.description}</td>
                          <td className={`p-3 text-right font-medium ${t.type === "despesa" ? "text-destructive" : "text-emerald-600"}`}>
                            {fmt.format(t.value)}
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={processing}
                              onClick={() => handleUndoSingle(t.id)}
                              className="gap-1"
                            >
                              <Undo2 className="h-3.5 w-3.5" /> Desfazer
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {reconciledAccountTx.length === 0 && (
                        <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhum lançamento conciliado nesta conta.</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <HistoryTable history={history} transactions={transactions} onUndo={handleUndoImport} processing={processing} />
            </TabsContent>
          </Tabs>
        )}

        {!accountId && history.length > 0 && (
          <HistoryTable history={history} transactions={transactions} onUndo={handleUndoImport} processing={processing} />
        )}

        {/* Quick-create modal pre-filled from OFX */}
        <Dialog open={!!createForOFX} onOpenChange={(o) => !o && setCreateForOFX(null)}>
          <DialogContent size="huge" className="max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Criar lançamento a partir do OFX</DialogTitle>
            </DialogHeader>
            {createForOFX && (
              <TransactionForm
                defaultValues={{
                  description: createForOFX.memo || createForOFX.name || "Importado OFX",
                  value: String(Math.abs(createForOFX.amount)),
                  type: createForOFX.amount < 0 ? "despesa" : "receita",
                  accountId,
                  issueDate: createForOFX.date,
                  dueDate: createForOFX.date,
                  paymentDate: createForOFX.date,
                  settled: true,
                  parcelMode: "total",
                }}
                lockType={createForOFX.amount < 0 ? "despesa" : "receita"}
                onSubmitted={async () => {
                  setCreateForOFX(null);
                  await forceRefresh();
                  if (ofxData) recompute(ofxData.transactions, accountId);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StatTile({ label, value, tone, icon }: { label: string; value: number; tone?: "success" | "warning" | "muted"; icon?: React.ReactNode }) {
  const toneClass =
    tone === "success" ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400" :
    tone === "warning" ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400" :
    tone === "muted" ? "border-muted bg-muted/30 text-muted-foreground" :
    "border-primary/30 bg-primary/5 text-primary";
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function SingleMatchPill({ tx }: { tx: any }) {
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="font-normal">
        {formatDateBR(tx.dueDate)}
      </Badge>
      <span className="truncate max-w-[260px]">{tx.description}</span>
      <span className="text-muted-foreground">{fmt.format(tx.value)}</span>
    </div>
  );
}

function HistoryTable({ history, transactions, onUndo, processing }: { history: any[]; transactions: any[]; onUndo: (id: string) => void; processing: boolean }) {
  // Conta ao vivo quantos lançamentos ainda permanecem conciliados por importação
  const liveCounts = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const t of transactions) {
      if (t.reconciled && t.ofxImportId) {
        map.set(t.ofxImportId, (map.get(t.ofxImportId) || 0) + 1);
      }
    }
    return map;
  }, [transactions]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Histórico de importações</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">Data</th>
              <th className="p-3">Arquivo</th>
              <th className="p-3">Período</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-right">Conciliadas</th>
              <th className="p-3 text-right">Pendentes</th>
              <th className="p-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => {
              const conciliadas = liveCounts.get(h.id) || 0;
              const pendentes = Math.max((h.total_transactions || 0) - conciliadas, 0);
              return (
                <tr key={h.id} className="border-t">
                  <td className="p-3 whitespace-nowrap">{formatDateBR(h.created_at?.slice(0, 10))}</td>
                  <td className="p-3">{h.file_name}</td>
                  <td className="p-3 whitespace-nowrap">{formatDateBR(h.period_start)} – {formatDateBR(h.period_end)}</td>
                  <td className="p-3 text-right">{h.total_transactions}</td>
                  <td className="p-3 text-right text-emerald-600">{conciliadas}</td>
                  <td className="p-3 text-right text-amber-600">{pendentes}</td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" disabled={processing || conciliadas === 0} onClick={() => onUndo(h.id)} className="gap-1">
                      <Undo2 className="h-3.5 w-3.5" /> Desfazer
                    </Button>
                  </td>
                </tr>
              );
            })}
            {history.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhuma importação ainda.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
